import { useState } from "react";
import { Form } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { useEligibility } from "@/contexts/EligibilityContext";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import type { VisaPathway } from "./VisaPathwaySelection";
import PersonalInfo from "./steps/PersonalInfo";
import Education from "./steps/Education";
import Experience from "./steps/Experience";
import Skills from "./steps/Skills";
import Achievements from "./steps/Achievements";
import ProfessionalQualifications from "./steps/ProfessionalQualifications";
import Recognition from "./steps/Recognition";
import Summary from "./steps/Summary";
import FormStepIndicator from "./FormStepIndicator";
import CVUpload from "./CVUpload";

interface EligibilityAssessmentFormProps {
  visaPathway: VisaPathway;
  onBack: () => void;
  onComplete: (data: any) => void;
}

const defaultValues = {
  personalInfo: {
    fullName: "",
    phone: "",
    email: "",
    targetRoles: "",
    portfolioUrl: "",
    linkedinUrl: "",
    additionalInfo: "",
    dataConsent: false
  },
  education: {
    degree: "",
    field: "",
    institution: "",
    graduationYear: ""
  },
  experience: {
    nonQualifyingProfession: "not_sure",
    technologyExperience: "none"
  },
  skills: [],
  achievements: [],
  recognition: {
    excellenceAwards: "none",
    awardRecognition: "national"
  },
  qualifications: {
    qualifyingProfession: "not_sure",
    techActivityPast5Years: "no",
    leadershipExperience: "not_sure",
    seniorContribution: "not_sure",
    professionalReferences: "no",
    executiveExperience: "not_sure",
    mentorshipExperience: "not_sure",
    academicResearch: "not_sure",
    speakingEngagements: "not_sure",
    publishedWork: "not_sure",
    peerAssessment: "not_sure"
  }
};

const EligibilityAssessmentForm = ({ 
  visaPathway,
  onBack,
  onComplete 
}: EligibilityAssessmentFormProps) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cvFile, setCVFile] = useState<File | null>(null);
  const form = useForm({ defaultValues });
  const { setTemporaryData, saveAssessment } = useEligibility();
  const { toast } = useToast();
  const totalSteps = 7;

  const handleSubmit = async (data: any) => {
    if (!cvFile) {
      toast({
        variant: "destructive",
        title: "CV Required",
        description: "Please upload your CV before submitting the assessment.",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      const assessmentData = {
        ...data,
        visaPathway: {
          id: visaPathway.id,
          title: visaPathway.title
        },
        cv: {
          name: cvFile.name,
          size: cvFile.size,
          type: cvFile.type
        }
      };

      await saveAssessment(assessmentData);
      onComplete(assessmentData);
      
      toast({
        title: "Assessment Completed",
        description: "Your eligibility assessment has been saved successfully.",
      });
    } catch (error) {
      console.error("Form submission error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was a problem saving your assessment. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    const currentData = form.getValues();
    setTemporaryData(currentData);
    setStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handlePrevious = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <>
            <CVUpload 
              onFileSelect={setCVFile}
              selectedFile={cvFile}
              className="mb-6"
            />
            <PersonalInfo form={form} visaPathway={visaPathway} />
          </>
        );
      case 2:
        return <Education form={form} visaPathway={visaPathway} />;
      case 3:
        return <Experience form={form} visaPathway={visaPathway} />;
      case 4:
        return <Skills form={form} visaPathway={visaPathway} />;
      case 5:
        return <ProfessionalQualifications form={form} visaPathway={visaPathway} />;
      case 6:
        return <Recognition form={form} visaPathway={visaPathway} />;
      case 7:
        return <Summary data={form.getValues()} visaPathway={visaPathway} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={onBack}
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Pathways
        </Button>
        <div className="flex-1">
          <h2 className="text-xl font-semibold">{visaPathway.title}</h2>
          <p className="text-sm text-muted-foreground">{visaPathway.description}</p>
        </div>
      </div>

      <FormStepIndicator currentStep={step} totalSteps={totalSteps} />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          {renderStep()}

          <div className="flex justify-between pt-6">
            {step > 1 && (
              <Button 
                type="button" 
                variant="outline" 
                onClick={handlePrevious}
                disabled={isSubmitting}
              >
                Previous
              </Button>
            )}
            <Button 
              type="submit"
              className="ml-auto"
              disabled={isSubmitting}
              onClick={step < totalSteps ? handleNext : undefined}
            >
              {isSubmitting ? "Saving..." : step === totalSteps ? "Submit Assessment" : "Next"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default EligibilityAssessmentForm;